import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import SiteHeader from './SiteHeader';
import SiteFooter from './SiteFooter';
import { TierCards } from './reserve/TierCards';
import { ReserveDetailsForm } from './reserve/ReserveDetailsForm';
import { VenmoPaymentStep } from './reserve/VenmoPaymentStep';
import { trackPageView } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = ['Choose a tier', 'Your details', 'Pay with Venmo'];

const Reserve = () => {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState(null);
  const [reservation, setReservation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    trackPageView('Reserve');
    axios.get(`${BACKEND_URL}/api/reserve/tiers`).then(r => setData(r.data)).catch(() => setData({ open: false, tiers: [] }));
  }, []);

  const step = reservation ? 2 : tier ? 1 : 0;

  const submit = async (form) => {
    setSubmitting(true); setError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/reserve`, { ...form, tier_id: tier.id });
      setReservation(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong — please try again.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-cream-50" data-testid="reserve-page">
      <SiteHeader active="reserve" />

      <section className="relative overflow-hidden bg-[#1a0d10] text-white py-16 md:py-24 px-4 md:px-6">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(124,29,51,0.25),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(217,161,91,0.12),transparent_60%)]" />
        <div className="absolute inset-0 grain" />
        <div className="max-w-5xl mx-auto relative z-10">
          <p className="eyebrow text-white/50 mb-5">ROJO Recovery Retreat · {data?.season || '2027'}</p>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-medium leading-[0.95] tracking-tighter mb-6">
            Reserve your <span className="bg-gradient-to-br from-[#c98a4b] via-[#d9a15b] to-[#f0d3a0] bg-clip-text text-transparent italic">bed.</span>
          </h1>
          <p className="text-sm md:text-base text-white/70 max-w-2xl leading-relaxed">
            Pick the kind of room you want, tell us who you are, and pay in the Venmo app. We hold your bed for {data?.hold_hours || 48} hours while the payment clears — then it's yours.
          </p>
          <ol className="mt-10 flex flex-wrap gap-2 md:gap-3" data-testid="reserve-steps">
            {STEPS.map((s, i) => (
              <li key={s} className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${i === step ? 'bg-[#d9a15b] text-[#1a0d10] border-[#d9a15b]' : i < step ? 'bg-white/10 text-white/80 border-white/20' : 'text-white/40 border-white/10'}`}>
                <span className="font-mono">{i + 1}</span> {s}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-12 md:py-20 px-4 md:px-6">
        <div className={`mx-auto ${step === 0 ? 'max-w-6xl' : 'max-w-3xl'}`}>
          {!data ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-wine" /></div>
          ) : step === 2 ? (
            <VenmoPaymentStep reservation={reservation} onUpdate={setReservation} />
          ) : step === 1 ? (
            <ReserveDetailsForm tier={tier} holdHours={data.hold_hours} onBack={() => setTier(null)} onSubmit={submit} submitting={submitting} error={error} />
          ) : !data.open ? (
            <div className="bg-white rounded-2xl shadow-xl p-10 text-center border border-gray-200" data-testid="reserve-closed">
              <p className="font-display text-3xl text-gray-900 mb-2">Reservations aren't open yet.</p>
              <p className="text-gray-600">Check back soon, or email <a className="text-wine underline" href="mailto:info@rojoretreat.com">info@rojoretreat.com</a>.</p>
            </div>
          ) : (
            <>
              <div className="mb-10 md:mb-12 max-w-2xl">
                <p className="eyebrow text-gray-400 mb-3">Step 1 · Choose a tier</p>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 mb-3">Three ways to stay.</h2>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">You're reserving a bed, not a whole room. Exact room and roommate assignments happen closer to the retreat, just like this year.</p>
              </div>
              {data.alumni_only_now && (
                <div className="mb-8 flex items-start gap-3 rounded-xl bg-[#1a0d10] text-white px-5 py-4 border border-[#d9a15b]/40" data-testid="alumni-only-notice">
                  <span className="mt-0.5 w-2 h-2 rounded-full bg-[#d9a15b] shrink-0 animate-pulse" />
                  <p className="text-sm leading-relaxed"><strong className="text-[#f0d3a0]">Early access for {data.archive_season} attendees.</strong> Use the email you registered with last year. Reservations open to everyone on {new Date(data.public_opens_at).toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.</p>
                </div>
              )}
              <TierCards tiers={data.tiers} holdHours={data.hold_hours} onSelect={(t) => { setTier(t); window.scrollTo({ top: 0, behavior: 'smooth' }); }} selectedId={tier?.id} />
              <p className="text-center text-xs text-gray-400 mt-10">Need help paying? Scholarship support is available — <a href="/scholarship" className="underline text-wine">learn more</a>.</p>
            </>
          )}
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Reserve;
